import { SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useUpdateMediaAiTrainingDataApi } from '@/common/api/modules/mediaAiTrainingData'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { useShowSavedToast } from '@/common/app/toast'
import {
  MediaAiTrainingData,
  RESTRICTIONS_MAX_LENGTH
} from '@/common/model/media/mediaAiTrainingData'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  CommonRulesSchemaType,
  commonRulesNameLabelMapping,
  commonRulesSchema,
  convertCommonRulesSchemaToVars,
  convertCommonRulesToSchema
} from '@/components/container/form/modules/media/aiTrainingData/commonRulesSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextArea from '@/components/container/form/widgets/FormTextArea'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'
import AppTitleBadge, { AppTitleBadgeType } from '@/components/container/layout/AppTitleBadge'

interface Props {
  data: MediaAiTrainingData
  onRefetch?(): void
}

// 共通ルール（制約・禁止事項）の登録。サイトごとに1件で、保存のたびに上書きする。
// 空にして保存すると未登録の状態へ戻り、AI記事生成は制約を付与しない従来生成に戻る。
// 保存は部分更新のため、この画面からの保存で記事HTMLの登録内容は変化しない。
const CommonRulesForm = ({ data, onRefetch }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<CommonRulesSchemaType>({
    resolver: zodResolver(commonRulesSchema),
    values: convertCommonRulesToSchema(data),
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, commonRulesNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: true })

  const { trigger } = useUpdateMediaAiTrainingDataApi({
    onSuccess: () => {
      showSaved('共通ルール')
      onRefetch?.()
    }
  })

  const onSubmit: SubmitHandler<CommonRulesSchemaType> = (formData) =>
    trigger(convertCommonRulesSchemaToVars(formData))

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageTitle
        title={'共通ルール'}
        badgeNode={<AppTitleBadge type={AppTitleBadgeType.MEDIA} />}
      />
      {isDirty && (
        <TopBar
          status={TopBarStatus.CHANGE}
          captionText='未反映の変更があります'
          onCancel={() => reset(convertCommonRulesToSchema(data))}
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading subText='AI記事生成時に禁止したい内容や、法律や制約などの影響で表現してほしくないものなどを登録してください。'>
            制約・禁止事項
          </Heading>
          <AreaContent>
            <FormRow direction='horizon' gap={4} size={TextBoxSize.FULL}>
              <FormRow direction='vertical' gap={2}>
                <FormTextArea
                  name='restrictions'
                  register={register('restrictions')}
                  control={control}
                  error={!!errors.restrictions?.message}
                  maxLength={RESTRICTIONS_MAX_LENGTH}
                  placeholder={
                    '例：\n・医薬効果を断定する表現は使わない\n・競合他社を誹謗中傷しない\n・未確認の数値や実績を記載しない'
                  }
                />
                <FormError message={errors.restrictions?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          保存する
        </Button>
      </PageBottom>
    </form>
  )
}

export default CommonRulesForm
